// Firearm catalog and bound-book helpers (NOT FFL — see ffl.utils.ts).

import {
  AvailableFirearm,
  BoundBookEntry,
  Firearm,
} from '../models/general';
import { Questions } from '../models/constants';

export function checkIsFirearmInList(sharedService: any, firearm_upc: string) {
  let isPresent: Firearm = sharedService.firearm_list.find((item) => {
    return item.firearm_upc == firearm_upc;
  });
  return isPresent;
}

export function findFirearm(sharedService: any, firearm_upc: string) {
  for (const item of sharedService.firearm_list) {
    if (item.firearm_upc === firearm_upc) {
      const firearm = item;
      sharedService.openYesNoModal(Questions.use_this_firearm, firearm, true);
    }
  }
}

export function availableFirearmsForDispo(
  bound_book: BoundBookEntry[],
): AvailableFirearm[] {
  let availableFirearms: AvailableFirearm[] = [];
  for (const item of bound_book) {
    if (item.d_disposed_to === null) {
      availableFirearms.push({
        firearm_upc: item.a_firearm_upc,
        serial_number: item.a_serial_number,
      });
    }
  }
  return availableFirearms;
}

export function createDistinctManufactureList(
  firearm_type: string,
  sharedService: any,
): string[] {
  const firearmList = sharedService.firearm_list || [];

  // Try to get specific matches first
  let manufacturers = firearmList
    .filter(
      (f: any) =>
        !firearm_type ||
        (f.firearm_type || '').toUpperCase() === firearm_type.toUpperCase(),
    )
    .map((f: any) => f.manufacture || f.manufacturer)
    .filter((m) => !!m);

  // Fallback to all manufacturers if specific type has no results
  if (manufacturers.length === 0 && firearm_type) {
    manufacturers = firearmList
      .map((f: any) => f.manufacture || f.manufacturer)
      .filter((m) => !!m);
  }

  // Final fallback for empty databases
  if (manufacturers.length === 0) {
    manufacturers = [
      'GLOCK',
      'SMITH & WESSON',
      'SIG SAUER',
      'RUGER',
      'REMINGTON',
      'WINCHESTER',
      'COLT',
      'BERETTA',
      'BROWNING',
      'TAURUS',
      'CZ',
      'SPRINGFIELD ARMORY',
      'MOSSBERG',
      'WALTHER',
      'HENRY',
      'SAVAGE ARMS',
      'KIMBER',
      'H&K',
    ];
  }

  return Array.from(new Set<string>(manufacturers)).sort((a: string, b: string) =>
    a.localeCompare(b),
  );
}

export function createDistinctFirearmCaliberList(
  firearm_type: string,
  sharedService: any,
  manufacture?: string,
  model?: string,
): string[] {
  const firearmList = sharedService.firearm_list || [];
  const calibers = firearmList
    .filter((f: any) => {
      const matchesType =
        !firearm_type ||
        (f.firearm_type || '').toUpperCase() === firearm_type.toUpperCase();
      const matchesManufacture =
        !manufacture ||
        (f.manufacture || f.manufacturer || '').toUpperCase() ===
          manufacture.toUpperCase();
      const matchesModel =
        !model || (f.model || '').toUpperCase() === model.toUpperCase();
      return matchesType && matchesManufacture && matchesModel;
    })
    .map((f: any) => f.caliber_gauge)
    .filter((c) => !!c);
  return Array.from(new Set<string>(calibers)).sort((a: string, b: string) =>
    a.localeCompare(b),
  );
}

export function createDistinctManufactureModelList(
  manufacturer: string,
  sharedService: any,
): string[] {
  const firearmList = sharedService.firearm_list || [];
  const manufacture_models = firearmList
    .filter(
      (f: any) =>
        (f.manufacture || f.manufacturer || '').toUpperCase() ===
        manufacturer.toUpperCase(),
    )
    .map((f: any) => f.model)
    .filter((m) => !!m);
  return Array.from(new Set<string>(manufacture_models)).sort(
    (a: string, b: string) => a.localeCompare(b),
  );
}

export function getMinimumAgeForFirearm(
  bound_book_ids: string[],
  sharedService: any,
): number {
  const ageRestrictedTypes = [
    'Pistol',
    'Revolver',
    'Silencer',
    'Machine Gun',
    'Short Barreled Rifle',
    'Short Barreled Shotgun',
    'Destructive Device',
  ];

  const ages = bound_book_ids.map((id) => {
    const entry = sharedService.bound_book.find(
      (item) => item.bound_book_id === id,
    );
    if (entry && ageRestrictedTypes.includes(entry.a_firearm_type)) {
      return 21;
    }
    return 18;
  });

  return Math.max(...ages);
}
